import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  LinearProgress,
  Grid,
  IconButton,
  Tooltip,
  styled,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEye,
  faTrashCan,
  faGraduationCap,
  faLanguage,
  faBrain,
  faHeart,
  faStar,
  faChartLine,
  faCity,
  faMapMarkerAlt,
  faCalendarAlt,
  faClock,
  faHandshake,
} from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { disponibilitesList2, disponibleSousList2 } from '../../data'
import moment from 'moment'

const MatchingCard = styled(Card)(({ theme }) => ({
  marginBottom: '16px',
  borderRadius: '12px',
  border: '1px solid rgba(102, 126, 234, 0.15)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    borderColor: 'rgba(102, 126, 234, 0.3)',
  },
}))

const ScoreChip = styled(Chip)(({ theme, score }) => {
  const getColor = (score) => {
    if (score >= 80) return { bg: '#4caf50', color: 'white' }
    if (score >= 60) return { bg: '#ff9800', color: 'white' }
    if (score >= 40) return { bg: '#f44336', color: 'white' }
    return { bg: '#9e9e9e', color: 'white' }
  }

  const colors = getColor(score)

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 'bold',
    fontSize: '14px',
    height: '32px',
    '& .MuiChip-label': {
      fontWeight: 'bold',
    },
  }
})

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: '2px',
  backgroundColor: 'rgba(102, 126, 234, 0.1)',
  color: '#667eea',
  border: '1px solid rgba(102, 126, 234, 0.2)',
  fontSize: '12px',
  height: '24px',
}))

const ProgressSection = styled(Box)(({ theme }) => ({
  marginBottom: '8px',
}))

const ProgressLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  marginBottom: '4px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const MatchingCandidatesTable = ({
  items,
  setId,
  loading,
  handleClick,
  handleRemove,
  matchingPost = '',
}) => {
  const { t, i18n } = useTranslation()
  const userData = useSelector((state) => state.userAuth.user)
  const theme = useTheme()
  const sizeChip = useMediaQuery(theme.breakpoints.down('sm')) ? 'small' : 'medium'
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '40vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!items || items.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '40vh',
          textAlign: 'center',
        }}
      >
        <FontAwesomeIcon
          icon={faGraduationCap}
          fontSize={48}
          color={theme.palette.grey[400]}
          style={{ marginBottom: '16px' }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No matching candidates found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your search criteria or job requirements
        </Typography>
      </Box>
    )
  }

  const getScoreColor = (score) => {
    if (score >= 80) return '#4caf50'
    if (score >= 60) return '#ff9800'
    if (score >= 40) return '#f44336'
    return '#9e9e9e'
  }

  const formatScore = (score) => {
    return Math.round(score)
  }

  const getAvailabilityLabel = (candidate) => {
    const mainDispo =
      disponibilitesList2.find(
        (dispo) => dispo.id === candidate?.candidat?.typeDisponibilite
      )?.titre[i18n.language] || ''

    let additionalInfo = ''

    if (
      candidate?.candidat?.typeDisponibilite === 2 &&
      candidate?.candidat?.disponibleSous
    ) {
      const sousDispo = disponibleSousList2.find(
        (dispoS) => dispoS.id === candidate?.candidat?.disponibleSous
      )?.titre[i18n.language]
      additionalInfo = sousDispo ? ` ${sousDispo}` : ''
    }

    if (
      candidate?.candidat?.typeDisponibilite === 4 &&
      candidate?.candidat?.disponible
    ) {
      additionalInfo = ` ${moment(candidate?.candidat?.disponible)
        .tz('Europe/Paris')
        .format('DD/MM/YYYY')}`
    }

    return mainDispo + additionalInfo
  }

  return (
    <Box sx={{ width: '100%' }}>
      {items.map((item, index) => {
        const candidate = item.candidate
        const scores = item.scores
        const overallScore = item.overallScore

        return (
          <MatchingCard key={index}>
            <CardContent sx={{ padding: '20px' }}>
              <Grid container spacing={3}>
                {/* Header Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          backgroundColor: 'primary.main',
                          fontSize: '20px',
                          fontWeight: 'bold',
                        }}
                      >
                        {candidate?.nom?.[0]}{candidate?.prenom?.[0]}
                      </Avatar> */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 'bold', mb: 0.5 }}
                        >
                          #Jober000{candidate?.candidat?.id}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: 'text.secondary',
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            fontSize={12}
                            color="black"
                          />
                          <Typography variant="body2">
                            {candidate?.ville}, {candidate?.pays}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScoreChip
                        label={`${formatScore(overallScore)}%`}
                        score={overallScore}
                      />
                      <Tooltip title="View Profile">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setId(item.id)
                            handleClick(item.id, item)
                          }}
                          sx={{
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(102, 126, 234, 0.2)',
                            },
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faEye}
                            color="#667eea"
                            fontSize={16}
                          />
                        </IconButton>
                      </Tooltip>
                      {(userData.data.role === 'ROLE_ADMIN' ||
                        userData.data.role === 'ROLE_SUPER_ADMIN') && (
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              onClick={() => handleRemove(candidate.id)}
                              sx={{
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                },
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faTrashCan}
                                color="#f44336"
                                fontSize={16}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                  </Box>
                </Grid>

                {/* Skills Section */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <FontAwesomeIcon icon={faGraduationCap} color="#667eea" />
                    {t('filter.competences')}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}
                  >
                    {candidate?.candidat?.competences
                      ?.slice(0, 5)
                      .map((skill, idx) => (
                        <SkillChip
                          key={idx}
                          label={skill.competence}
                          size="small"
                        />
                      ))}
                    {candidate?.candidat?.competences?.length > 5 && (
                      <Typography variant="caption" color="text.secondary">
                        +{candidate.candidat.competences.length - 5} more
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Languages Section */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <FontAwesomeIcon icon={faLanguage} color="#667eea" />
                    {t('filter.languages')}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}
                  >
                    {candidate?.langues?.slice(0, 4).map((lang, idx) => (
                      <SkillChip key={idx} label={lang.langue} size="small" />
                    ))}
                    {candidate?.langues?.length > 4 && (
                      <Typography variant="caption" color="text.secondary">
                        +{candidate.langues.length - 4} more
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Score Breakdown */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <FontAwesomeIcon icon={faChartLine} color="#667eea" />
                    {t('filter.matching')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      <ProgressSection>
                        <ProgressLabel>
                          <span>{t('filter.competences')}</span>
                          <span>
                            {formatScore(scores.competences.percentage)}%
                          </span>
                        </ProgressLabel>
                        <LinearProgress
                          variant="determinate"
                          value={scores.competences.percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getScoreColor(
                                scores.competences.percentage
                              ),
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {scores.competences.score}/{scores.competences.total}
                        </Typography>
                      </ProgressSection>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      <ProgressSection>
                        <ProgressLabel>
                          <span>{t('filter.competencesAnnexes')}</span>
                          <span>
                            {formatScore(scores.competencesAnnexes.percentage)}%
                          </span>
                        </ProgressLabel>
                        <LinearProgress
                          variant="determinate"
                          value={scores.competencesAnnexes.percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getScoreColor(
                                scores.competencesAnnexes.percentage
                              ),
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {scores.competences.score}/{scores.competences.total}{' '}
                          matched
                        </Typography>
                      </ProgressSection>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      <ProgressSection>
                        <ProgressLabel>
                          <span>{t('filter.languages')}</span>
                          <span>{formatScore(scores.langues.percentage)}%</span>
                        </ProgressLabel>
                        <LinearProgress
                          variant="determinate"
                          value={scores.langues.percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getScoreColor(
                                scores.langues.percentage
                              ),
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {scores.langues.score}/{scores.langues.total} matched
                        </Typography>
                      </ProgressSection>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      <ProgressSection>
                        <ProgressLabel>
                          <span>{t('filter.softskils')}</span>
                          <span>
                            {formatScore(scores.softskils.percentage)}%
                          </span>
                        </ProgressLabel>
                        <LinearProgress
                          variant="determinate"
                          value={scores.softskils.percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getScoreColor(
                                scores.softskils.percentage
                              ),
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {scores.softskils.score}/{scores.softskils.total}{' '}
                          matched
                        </Typography>
                      </ProgressSection>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
                      <ProgressSection>
                        <ProgressLabel>
                          <span>{t('filter.values')}</span>
                          <span>{formatScore(scores.valeurs.percentage)}%</span>
                        </ProgressLabel>
                        <LinearProgress
                          variant="determinate"
                          value={scores.valeurs.percentage}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getScoreColor(
                                scores.valeurs.percentage
                              ),
                              borderRadius: 4,
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {scores.valeurs.score}/{scores.valeurs.total} matched
                        </Typography>
                      </ProgressSection>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Additional Info */}
                <Grid item xs={12}>
                  <Box
                    sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}
                  >
                    <SkillChip
                      icon={
                        <FontAwesomeIcon
                          icon={faBrain}
                          style={{
                            color: '#8b5cf6',
                          }}
                        />
                      }
                      label={`${candidate?.candidat?.softskils?.length || 0
                        } ${t('filter.softskils')}`}
                      size={sizeChip}
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(102, 126, 234, 0.3)',
                        color: '#667eea',
                      }}
                    />
                    <SkillChip
                      icon={
                        <FontAwesomeIcon
                          icon={faHandshake}
                          style={{
                            color: '#06b6d4',
                          }}
                        />
                      }
                      label={`${candidate?.candidat?.valeurs?.length || 0} ${t(
                        'filter.values'
                      )}`}
                      size={sizeChip}
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(6, 182, 212, 0.3)',
                        color: '#06b6d4',
                      }}
                    />

                    <SkillChip
                      icon={
                        <FontAwesomeIcon
                          icon={faClock}
                          style={{
                            color: '#f59e0b',
                            fontSize: '18px',
                          }}
                        />
                      }
                      label={
                        candidate?.candidat?.typeTempsTravail === 'partiel'
                          ? t('box.timeWorkType2') +
                          ' (' +
                          candidate?.candidat?.tempPartiel +
                          '%)'
                          : t('box.timeWorkType1')
                      }
                      size={sizeChip}
                      sx={{
                        borderColor: 'rgba(255, 152, 0, 0.3)',
                        color: '#ff9800',
                        backgroundColor: 'rgba(255, 152, 0, 0.05)',
                      }}
                    />
                    <SkillChip
                      icon={
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          style={{
                            color: '#8b5cf6',
                            fontSize: '18px',
                            marginLeft: '10px',
                          }}
                        />
                      }
                      label={getAvailabilityLabel(candidate)}
                      size={sizeChip}
                      sx={{
                        borderColor: 'rgba(139, 92, 246, 0.3)',
                        color: '#8b5cf6',
                        backgroundColor: 'rgba(139, 92, 246, 0.05)',
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </MatchingCard>
        )
      })}
    </Box>
  )
}

export default MatchingCandidatesTable
